import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular/lazy';
import { ObserverService } from 'src/app/services/observer.service';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';

@Injectable()

export class QuoteService {

    constructor(
        public alertController: AlertController,
        private observerService: ObserverService,
        private router: Router,
        private authService: AuthService,
        private apiService: APIService
    ) { }

    acceptQuote(quote_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.acceptQuote(quote_id, data).then((quote) => {
                    this.observerService.publish("quote_updated", quote);
                    resolve()
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to accept a quote.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to accept a quote.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to accept a quote at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    addQuote(data) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                let idempotency_key = uuidv4();
                this.apiService.addQuote(data, idempotency_key).then((quote) => {
                    if(quote) {
                        this.observerService.publish('quote_added', {quote});
                    }
                    resolve(quote)
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to add a quote.");
                    } 
                    else if(error.status === 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to add a quote.");
                    } 
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to add a quote at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    cancelQuote(quote_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.cancelQuote(quote_id, data).then((quote) => {
                    this.observerService.publish("quote_updated", quote);
                    resolve()
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to cancel a quote.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to cancel a quote.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to cancel a quote at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    deleteQuote(quote_id) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.deleteQuote(quote_id).then(() => {
                    this.observerService.publish("quote_deleted", quote_id);
                    resolve();
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status == 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to delete a quote.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to delete a quote.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to delete a quote at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getQuote(quote_id, params?) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getQuote(quote_id, params).then((quote) => {
                    if(quote) {
                        resolve(quote);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to retrieve a quote.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to view a quote.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve a quote at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getQuotes(params) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getQuotes(params).then((quotes) => {
                    if(quotes) {
                        resolve(quotes);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to retrieve a list of quotes.");
                    } 
                    else if(error.status === 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to view quotes.");
                    } 
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve a list of quotes at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    sendQuoteEmail(quote_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.sendQuoteEmail(quote_id, data).then(() => {
                    this.getQuote(quote_id).then((quote) => {
                        this.observerService.publish("quote_updated", quote);
                    })
                    resolve()
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to send a quote email.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to send a quote email.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to send a quote email at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateQuote(quote_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateQuote(quote_id, data).then((quote) => {
                    this.observerService.publish("quote_updated", quote);
                    resolve()
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to update a quote.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to update a quote.");
                    }
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to update a quote at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ['OK'],
        });
        await alert.present();
    }

}